import { Target, Globe, ShieldCheck, Eye, Zap, HeartHandshake } from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { IMAGES } from "../data/translations";
import { Reveal } from "../components/Reveal";

const valueIcons = [ShieldCheck, Eye, Zap, HeartHandshake];

const About = () => {
  const { t } = useLanguage();
  const a = t.aboutPage;

  return (
    <div className="bg-ink" data-testid="about-page">
      <section className="relative pt-40 pb-16 bg-ink-surface border-b border-white/5 bg-grid" data-testid="about-header">
        <div className="container-site">
          <p className="eyebrow">{a.eyebrow}</p>
          <h1 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white max-w-3xl leading-[1.1]">{a.title}</h1>
          <p className="mt-6 max-w-2xl text-slate-400 leading-relaxed">{a.subtitle}</p>
        </div>
      </section>

      <section className="section-pad" data-testid="about-story">
        <div className="container-site grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <Reveal className="lg:col-span-6">
            <p className="eyebrow">{a.storyEyebrow}</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold text-white">{a.storyTitle}</h2>
            <p className="mt-6 text-slate-400 leading-relaxed">{a.storyP1}</p>
            <p className="mt-4 text-slate-400 leading-relaxed">{a.storyP2}</p>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-6">
              <img src={IMAGES.warehouse} alt="MVALOG logistics operations" className="relative w-full h-[380px] sm:h-[460px] object-cover border-2 border-brand" />
          </Reveal>
        </div>
      </section>

      <section className="pb-4" data-testid="about-mission-vision">
        <div className="container-site grid md:grid-cols-2 gap-5">
          {[
            { icon: Target, title: a.missionTitle, text: a.missionText },
            { icon: Globe, title: a.visionTitle, text: a.visionText },
          ].map(({ icon: Icon, title, text }) => (
            <Reveal key={title}>
              <div className="card-dark p-8 sm:p-10 h-full">
                <div className="w-12 h-12 bg-brand/10 border border-brand/30 flex items-center justify-center text-brand">
                  <Icon size={22} />
                </div>
                <h3 className="mt-6 font-display text-xl font-semibold text-white">{title}</h3>
                <p className="mt-3 text-slate-400 leading-relaxed">{text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section-pad" data-testid="about-values">
        <div className="container-site">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-white">{a.valuesTitle}</h2>
          </Reveal>
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {a.values.map((v, i) => {
              const Icon = valueIcons[i];
              return (
                <Reveal key={v.title} delay={i * 0.08}>
                  <div className="card-dark p-7 h-full" data-testid={`about-value-${i}`}>
                    <Icon size={24} className="text-brand" />
                    <h3 className="mt-5 font-display text-lg font-semibold text-white">{v.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-400">{v.text}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="pb-24" data-testid="about-founder">
        <div className="container-site">
          <Reveal>
            <div className="border-t border-white/10 pt-16">
              <div className="max-w-3xl">
                <p className="eyebrow">{a.founderEyebrow}</p>
                <h2 className="mt-4 font-brand text-3xl sm:text-4xl font-semibold tracking-[0.04em] text-white" data-testid="founder-name">
                  {a.founderName}
                </h2>
                <div className="mt-6 space-y-4">
                  {a.founderBio.map((p, i) => (
                    <p key={i} className="text-slate-400 leading-relaxed">{p}</p>
                  ))}
                </div>
                <p className="mt-8 font-brand text-sm uppercase tracking-[0.35em] text-brand/80">From Good to Great</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
